import cx from 'classnames';
import { Heading, Link, Text } from '@dx-ui/global-ui-prism';
import { useTranslation } from 'next-i18next';

type PhoneNumberLinkProps = React.ComponentProps<typeof Link> &
  ({ hint: string } | { hasStandardHint: boolean } | Record<never, never>);

function usePhoneNumberHint(props: PhoneNumberLinkProps): string {
  const { t } = useTranslation('osc-error-page');

  if ('hasStandardHint' in props && props.hasStandardHint === true) {
    return t('callMessage');
  }

  if ('hint' in props) {
    return props.hint;
  }

  return null as never;
}

function sanitizeLinkProps(props: PhoneNumberLinkProps): React.ComponentProps<typeof Link> {
  const linkProps: React.ComponentProps<typeof Link> = { ...props };

  if ('hasStandardHint' in linkProps) {
    delete linkProps.hasStandardHint;
  }

  return linkProps;
}

export function PhoneNumberLink(props: PhoneNumberLinkProps) {
  const { t } = useTranslation('osc-error-page');

  const hint = usePhoneNumberHint(props);

  return (
    <div className="grid grid-cols-1 sm:place-items-center" key={props.label + props.url}>
      <Link
        {...sanitizeLinkProps(props)}
        underline={false}
        className={cx('text--lg whitespace-nowrap font-bold uppercase', props.className)}
      >
        <span className="sr-only">{t('phone')}</span>
        {props.label}
      </Link>
      {hint ? (
        <Text size="sm" color="muted">
          {hint}
        </Text>
      ) : null}
    </div>
  );
}

export function PhoneNumberSection({
  children,
  title,
}: React.PropsWithChildren<{ title: string }>) {
  return (
    <div className="grid grid-cols-1">
      <Heading size="xl" level={2}>
        {title}
      </Heading>
      {children}
    </div>
  );
}
